import { FC, memo, RefObject, Suspense, useMemo, useRef } from 'react';
import { useOnClickOutside } from 'usehooks-ts';

import { WerklijnLegendLeft } from '@/apps/mwd-v2/types/legends';
import Check from '@/assets/icons/Check';
import DefectNietInzetbaar from '@/assets/icons/DefectNietInzetbaar';
import EyeFalse from '@/assets/icons/EyeFalse';
import Info from '@/assets/icons/Info';
import Ingeregeld from '@/assets/icons/Ingeregeld';
import Versterken from '@/assets/icons/Versterken';
import { useBamAuth } from '@/auth';
import { MwdContextMenuInformation } from '@/components/contextMenu/mwdContextMenu/types';
import useClientState from '@/state/useClientState';
import { bepaalDefectSoort } from '@/utils/helpers';

import useMwdActions from '../../hooks/useMwdActions';
import useSelectedStore from '../../state/selectors/useSelectedStore';
import { preventDefault } from '../../utils/mwdEvents/eventHelpers';

import ActionLink from './ActionLink';
import ContextMenuDeeltraject from './ContextMenuDeeltraject';
import ContextMenuOvergang from './ContextMenuOvergang';
import ExecuteActionLink from './ExecuteActionLink';

import styles from './ContextMenuV2.module.css';

const Li = ActionLink;
const OvergangMemo = memo(ContextMenuOvergang);

/**
 * Lightweight gate component — only subscribes to `showContextMenu`.
 * When the context menu is closed (99% of the time), no heavy hooks run.
 */
const ContextMenu: FC = () => {
  const showContextMenu = useClientState((state) => state.contextMenu.showContextMenu);

  if (!showContextMenu) return null;

  return <ContextMenuContent />;
};

/**
 * Heavy content — only mounted when the context menu is actually visible.
 * All hooks (useBamAuth, useMwdActions, useSelectedStore) only run while the menu is open.
 */
const ContextMenuContent: FC = () => {
  const { hasPermission } = useBamAuth();
  const isAllowedToMutate = hasPermission('mwd.mutate');
  const isAllowedToDefecten = hasPermission('mwd.defecten');
  const isAllowedToInregelingen = hasPermission('mwd.inregelingen');
  const contextKey = useClientState((state) => state.contextMenu.contextKey);
  const contextData = useClientState((state) => state.contextMenu.contextData);
  const mousePointer = useClientState((state) => state.contextMenu.mousePointer);
  const closeContextMenu = useClientState((state) => state.closeContextMenu);
  const { isMultiSelect, selectedDeeltrajecten, selectedWerklijnen } = useSelectedStore();
  const { verwijderGeselecteerdeWerklijnen } = useMwdActions();

  const hasDefect = useMemo(
    () =>
      contextKey === 'leftLegend' && (contextData as WerklijnLegendLeft)?.eenheid
        ? !!bepaalDefectSoort((contextData as WerklijnLegendLeft).eenheid, new Date())
        : false,
    [contextKey, contextData],
  );

  const isIngeregeld = contextKey === 'leftLegend' && (contextData as WerklijnLegendLeft)?.eenheid?.inregeling;
  const isManco = (contextData as MwdContextMenuInformation)?.details?.eenheidnummer === 'MANCO';

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref as RefObject<HTMLElement>, () => void closeContextMenu());

  return (
    <Suspense fallback={<div />}>
      <div
        ref={ref}
        id='mwdContextMenu'
        className={styles.contextMenuV2}
        data-testid={`contextMenu-${(contextData as MwdContextMenuInformation)?.details?.werklijnId ?? (contextData as WerklijnLegendLeft)?.werklijnId}`}
        onContextMenu={preventDefault}
        style={{
          left: `${(mousePointer?.x ?? 0) + 130}px`,
          top: `${mousePointer?.y ? mousePointer.y + 45 : 0}px`,
        }}
      >
        <ul className={styles.contextInnerMenuV2}>
          {contextKey === 'leftLegend' && (
            <>
              {!isMultiSelect && (
                <>
                  {isAllowedToMutate && (
                    <Li actionKey='versterken' icon={Versterken} shortcut='Alt+V'>
                      Versterk met dit materieel…
                    </Li>
                  )}
                  {isAllowedToDefecten && (
                    <>
                      {!isManco && (
                        <Li actionKey='niet-inzetbaar-vanaf' icon={DefectNietInzetbaar}>
                          Meld defect…
                        </Li>
                      )}
                      {hasDefect && !isManco && (
                        <Li actionKey='defect-gereedmelden' icon={Check}>
                          Meld defect(en) gereed…
                        </Li>
                      )}
                    </>
                  )}
                  {isAllowedToInregelingen && (
                    <>
                      {!isIngeregeld && !isManco && (
                        <Li actionKey='inregelen' icon={Ingeregeld} shortcut='Alt+I'>
                          Inregelen…
                        </Li>
                      )}
                      {isIngeregeld && (
                        <>
                          <Li actionKey='inregeling-wijzigen' icon={Ingeregeld} shortcut='Alt+W'>
                            Inregeling wijzigen…
                          </Li>
                          <Li actionKey='inregeling-verwijderen' icon={Ingeregeld} shortcut='Alt+R'>
                            Inregeling verwijderen…
                          </Li>
                        </>
                      )}
                    </>
                  )}
                  {!isManco && (
                    <Li actionKey='materieel-details' icon={Info}>
                      Toon materieel details
                    </Li>
                  )}
                </>
              )}
              <ExecuteActionLink
                actionKey='verwijderen-mwd'
                icon={EyeFalse}
                action={verwijderGeselecteerdeWerklijnen}
                shortcut='Delete'
              >
                {isMultiSelect ? `Verwijder ${selectedWerklijnen.length} werklijnen` : 'Verwijder werklijn'}
              </ExecuteActionLink>
            </>
          )}

          {contextKey === 'overgang' && (
            <OvergangMemo
              isAllowedToMutate={isAllowedToMutate}
              isAllowedToDefecten={isAllowedToDefecten}
              isMultiSelect={isMultiSelect}
              isManco={(contextData as MwdContextMenuInformation)?.details?.eenheidnummer === 'MANCO'}
            />
          )}

          {contextKey === 'deeltraject' && (
            <ContextMenuDeeltraject
              contextData={contextData as unknown as MwdContextMenuInformation}
              noSelectedDeeltrajecten={selectedDeeltrajecten.length}
              isAllowedToMutate={isAllowedToMutate}
              isAllowedToInregelingen={isAllowedToInregelingen}
              isAllowedToDefecten={isAllowedToDefecten}
              isMultiSelect={isMultiSelect}
              isVerkeerdMat={(contextData as MwdContextMenuInformation)?.verkeerdMat ?? false}
              isClaim={Boolean((contextData as MwdContextMenuInformation)?.isClaimContextMenu)}
              isContextMenu={(contextData as MwdContextMenuInformation)?.isContextMenu}
              isManco={(contextData as MwdContextMenuInformation)?.details?.eenheidnummer === 'MANCO'}
            />
          )}
        </ul>
      </div>
    </Suspense>
  );
};

export default ContextMenu;
